import React, {
	Dispatch,
	SetStateAction,
	useCallback,
	useEffect,
	useState,
} from "react";
import { Button, Icon, Loading } from "@alm/flow-ds/components";
import Notification from "./Notification";
import { NotificationConfig } from "records/Notification";
import PaginationButton from "../PaginationButton";
import api from "lib/api";
import { useTranslation } from "react-i18next";
import usePagedData from "hooks/usePagedData";
import { useNotifications } from "hooks/useNotifications";
import useUnreadCounts from "hooks/useUnreadCount";
import { useSelector } from "react-redux";
import { getNotifUnread } from "data/app/appSelectors";

export interface ApiNotificationProps extends NotificationConfig {
	_id: string;
}
interface NotificationsProps {
	setIsNotificationsTrayOpen?: Dispatch<SetStateAction<boolean>> | undefined;
}

function Notifications({ setIsNotificationsTrayOpen }: NotificationsProps) {
	const { t } = useTranslation();
	const [init, setInit] = useState(false);

	const handleUnreadCount = useUnreadCounts();
	const reduxUnreadCount = useSelector(getNotifUnread);

	// Hook to handle paging notifications
	const { state, setData, ready, addPage, patchState, loading } =
		usePagedData();

	// Hook to read/delete notifications
	const { handleAllRead, handleDelete, handleDeleteAll, handleRead } =
		useNotifications(state, patchState);

	/**
	 * Paging notifications
	 */
	const handlePagination = useCallback(() => {
		loading();
		api.notifications
			.getAll({ offset: state.offset, limit: state.limit })
			.then(data => {
				addPage(data.pageData);
				ready();
			})
			.catch(e => console.error(e));
	}, [addPage, loading, ready, state.limit, state.offset]);

	/**
	 * Marking all notifications as read
	 */
	const handleMarkAllRead = useCallback(async () => {
		try {
			await handleAllRead();
		} catch (error) {
			console.error("Failed to mark them all as read");
		}
	}, [handleAllRead]);

	const handleClearAll = useCallback(async () => {
		try {
			await handleDeleteAll();
			await handleUnreadCount();
		} catch (error) {
			console.error("Failed to deleteAll");
		}
	}, [handleDeleteAll, handleUnreadCount]);
	/**
	 * If the element clicked is an anchor tag then close the notifications tray
	 */
	const handleClick = useCallback(
		e => {
			if (e?.target?.nodeName === "A") {
				setIsNotificationsTrayOpen && setIsNotificationsTrayOpen(false);
			}
		},
		[setIsNotificationsTrayOpen]
	);

	/**
	 * Get all notifications when component is first mounted
	 */
	useEffect(() => {
		api.notifications
			.getAll()
			.then(data => {
				setData(data.pageData);
				ready();
				setInit(true);
			})
			.catch(e => console.error(e));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Get notifications element so we can add event listener to it to handle below
	const notifications = document.querySelector(".notifications__sidetray");

	/**
	 * Listen to clicks on the notifications tray
	 */
	useEffect(() => {
		notifications?.addEventListener("click", handleClick);
		return () => {
			notifications?.removeEventListener("click", handleClick);
		};
	}, [handleClick, notifications, setIsNotificationsTrayOpen]);

	if (!init) return <Loading className="mt-4 elem__loader--md" />;

	return state.results.length > 0 ? (
		<>
			<div className="notifications__read-section">
				<h3>{t("main.notifications.heading")}</h3>
				{reduxUnreadCount > 0 ? (
					<Button
						aria-label={t("main.notifications.markAllAsRead")}
						name="markAllRead"
						label={t("main.notifications.markAllAsRead")}
						className="btn-basic--primary"
						onClick={handleMarkAllRead}
					/>
				) : (
					<Button
						aria-label={t("main.notifications.clearAll")}
						name="clearAll"
						label={t("main.notifications.clearAll")}
						className="btn-basic--primary"
						onClick={handleClearAll}
					/>
				)}
			</div>
			<div className="notifications__container">
				<ul className="notifications__list">
					{state.results
						? state.results.map(
								(
									notification: ApiNotificationProps,
									index: number
								) => {
									const props = {
										...notification,
										id: notification._id,
										key: notification._id,
										handleRead: () =>
											handleRead(notification._id, index),
										handleDelete: () =>
											handleDelete(notification._id),
									};
									return <Notification {...props} />;
								}
						  )
						: null}
				</ul>
				<div className="notifications-more-btn">
					<PaginationButton
						pageType="notifications"
						loading={state.loading}
						page={handlePagination}
						hasMore={state.hasMore}
					/>
				</div>
			</div>
		</>
	) : (
		<div className="notifications__empty">
			<div className="notifications__empty-icon">
				<Icon type="bell-slash" size="3x" />
			</div>
			<div className="notifications__empty-title">
				{t("main.notifications.noNewNotifications")}
			</div>
		</div>
	);
}

export default Notifications;
